import React from 'react';
import { Gift, Sparkles } from 'lucide-react';
import { fetchAdminFeedbackForms } from '../../lib/admin';
import { friendlyRequestError } from '../../lib/http';
import type { AdminFeedbackForm, AdminFeedbackResponse } from '../../lib/admin';

const AUTO_REFRESH_MS = 10000;
const PRIZE_FEEDBACK_FORM_ID = '2026-conference-feedback';

const DRAW_DEFINITIONS = [
  { id: 'overall-1', title: 'Prize 1' },
  { id: 'overall-2', title: 'Prize 2' },
  { id: 'overall-3', title: 'Prize 3' },
  { id: 'overall-4', title: 'Prize 4' },
  { id: 'overall-5', title: 'Prize 5' }
] as const;

type DrawDefinition = (typeof DRAW_DEFINITIONS)[number];
type DrawId = DrawDefinition['id'];
type DrawStatus = 'idle' | 'running' | 'complete' | 'empty';

interface DrawEntrant {
  key: string;
  name: string;
  company: string;
  formId: string;
  responseId: string;
}

type DrawState = Record<DrawId, {
  status: DrawStatus;
  entrant?: DrawEntrant;
}>;

function createInitialDrawState(): DrawState {
  return DRAW_DEFINITIONS.reduce((state, definition) => {
    state[definition.id] = { status: 'idle' };
    return state;
  }, {} as DrawState);
}

function orderedQuestions(form: AdminFeedbackForm) {
  return [...(form.questions ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function isAnswered(value: unknown) {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value != null && value !== '';
}

function isCompleteResponse(form: AdminFeedbackForm, response: AdminFeedbackResponse) {
  if (!response.submittedAt) return false;

  const requiredQuestions = orderedQuestions(form).filter((question) => question.required);
  return requiredQuestions.every((question) => isAnswered(response.answers?.[question.id]));
}

function isUserMemberResponse(response: AdminFeedbackResponse) {
  return String(response.profile?.roleType || '').trim().toLowerCase() === 'user';
}

function responseEntrant(form: AdminFeedbackForm, response: AdminFeedbackResponse): DrawEntrant {
  const profile = response.profile;
  return {
    key: String(response.profileId || response.id || `${form.id}:${response.id}`),
    name: String(profile?.displayName || response.profileId || 'Unknown respondent'),
    company: String(profile?.companyName || ''),
    formId: form.id,
    responseId: response.id
  };
}

function uniqueEntrants(entries: DrawEntrant[]) {
  const byKey = new Map<string, DrawEntrant>();
  entries.forEach((entry) => {
    if (!byKey.has(entry.key)) byKey.set(entry.key, entry);
  });
  return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function buildDrawPools(forms: AdminFeedbackForm[]) {
  const overall: DrawEntrant[] = [];

  forms.forEach((form) => {
    if (form.id !== PRIZE_FEEDBACK_FORM_ID) return;

    (form.responses ?? []).forEach((response) => {
      if (!isCompleteResponse(form, response)) return;
      if (!isUserMemberResponse(response)) return;
      overall.push(responseEntrant(form, response));
    });
  });

  return {
    overall: uniqueEntrants(overall)
  };
}

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

function MysteryPrizeGlyph(props: {
  running?: boolean;
  complete?: boolean;
  disabled?: boolean;
  title: string;
  onDraw: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onDraw}
      disabled={props.disabled}
      aria-label={`Draw ${props.title}`}
      className={classNames(
        'relative mx-auto my-[clamp(0.6rem,1.6vh,1.3rem)] flex h-[clamp(6.5rem,10vw,10rem)] w-[clamp(6.5rem,10vw,10rem)] items-center justify-center rounded-[clamp(1.2rem,1.5vw,2rem)] border-0 text-white transition focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-red-600/30',
        props.complete ? 'bg-accent' : 'bg-zinc-900',
        props.running ? 'animate-pulse' : '',
        props.disabled ? 'cursor-default' : 'cursor-pointer hover:brightness-110'
      )}
    >
      <Gift className="h-[clamp(4rem,6vw,6rem)] w-[clamp(4rem,6vw,6rem)]" aria-hidden />
      <Sparkles className="absolute right-[18%] top-[16%] h-[clamp(1rem,1.4vw,1.7rem)] w-[clamp(1rem,1.4vw,1.7rem)]" aria-hidden />
      {!props.complete ? (
        <span className="absolute -bottom-2 -right-2 flex h-[clamp(2.5rem,3.1vw,3.5rem)] w-[clamp(2.5rem,3.1vw,3.5rem)] items-center justify-center rounded-full border-4 border-white bg-white text-[clamp(1.55rem,2.3vw,2.4rem)] font-black leading-none text-zinc-900 shadow-sm">
          ?
        </span>
      ) : null}
    </button>
  );
}

function PrizeBox(props: {
  definition: DrawDefinition;
  state: DrawState[DrawId];
  disabled: boolean;
  onDraw: () => void;
}) {
  const { definition, state } = props;
  const complete = state.status === 'complete' && state.entrant;
  const running = state.status === 'running';
  const empty = state.status === 'empty';

  return (
    <article className="flex h-full min-h-0 flex-col justify-between rounded-3xl border border-zinc-200 bg-zinc-50 p-[clamp(0.8rem,1.3vw,1.5rem)] transition hover:border-zinc-300 hover:bg-white">
      <div className="flex min-h-[clamp(2rem,3.2vh,3.8rem)] items-start justify-center">
        <div className="text-center text-[clamp(1.35rem,1.7vw,2.2rem)] font-bold leading-tight text-zinc-900">
          {definition.title}
        </div>
      </div>

      <MysteryPrizeGlyph
        title={definition.title}
        running={running}
        complete={Boolean(complete)}
        disabled={props.disabled}
        onDraw={props.onDraw}
      />

      <div className="flex min-h-[clamp(6rem,13vh,9rem)] flex-col items-center justify-center rounded-2xl border border-zinc-200 bg-white p-[clamp(0.8rem,1.2vw,1.3rem)] text-center">
        {complete ? (
          <>
            <div className="max-w-full text-[clamp(1.45rem,2vw,2.4rem)] font-extrabold leading-[1.08] text-zinc-900 [overflow-wrap:anywhere]">
              {state.entrant?.name}
            </div>
            <div className="mt-2 max-w-full text-[clamp(1.05rem,1.25vw,1.55rem)] leading-tight text-zinc-600 [overflow-wrap:anywhere]">
              {state.entrant?.company || 'Company not recorded'}
            </div>
          </>
        ) : running ? (
          <div className="flex min-h-[clamp(3.8rem,8vh,5.6rem)] items-center justify-center">
            <div className="h-[clamp(3rem,4vw,4.6rem)] w-[clamp(3rem,4vw,4.6rem)] animate-spin rounded-full border-[clamp(0.35rem,0.45vw,0.55rem)] border-zinc-200 border-t-accent" />
          </div>
        ) : empty ? (
          <div className="text-[clamp(1.05rem,1.15vw,1.45rem)] font-semibold text-zinc-600">No eligible responses</div>
        ) : null}
      </div>
    </article>
  );
}

export default function AdminPrizesPage() {
  const [forms, setForms] = React.useState<AdminFeedbackForm[]>([]);
  const [drawState, setDrawState] = React.useState<DrawState>(() => createInitialDrawState());
  const [excludedKeys, setExcludedKeys] = React.useState<Set<string>>(() => new Set());
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const refreshInFlightRef = React.useRef(false);
  const timeoutRef = React.useRef<number | null>(null);

  const pools = React.useMemo(() => buildDrawPools(forms), [forms]);
  const chosenKeys = React.useMemo(() => {
    const keys = new Set<string>(excludedKeys);
    Object.values(drawState).forEach((state) => {
      if (state.status === 'complete' && state.entrant) keys.add(state.entrant.key);
    });
    return keys;
  }, [drawState, excludedKeys]);
  const drawInProgress = Object.values(drawState).some((state) => state.status === 'running');

  const clearTimers = React.useCallback(() => {
    if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const refresh = React.useCallback(async (options?: { silent?: boolean }) => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    if (!options?.silent) setLoading(true);
    if (!options?.silent) setError(null);

    try {
      setForms(await fetchAdminFeedbackForms());
      setError(null);
    } catch (err) {
      setError(friendlyRequestError(err, 'Could not load feedback responses.'));
    } finally {
      refreshInFlightRef.current = false;
      if (!options?.silent) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => clearTimers, [clearTimers]);

  React.useEffect(() => {
    if (drawInProgress) return undefined;
    const intervalId = window.setInterval(() => {
      void refresh({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [drawInProgress, refresh]);

  function eligibleFor(definition: DrawDefinition) {
    void definition;
    return pools.overall.filter((entry) => !chosenKeys.has(entry.key));
  }

  function runDraw(definition: DrawDefinition) {
    if (drawInProgress) return;

    const previousWinner = drawState[definition.id].entrant;
    const pool = eligibleFor(definition);
    const availablePool = previousWinner
      ? pool.filter((entry) => entry.key !== previousWinner.key)
      : pool;

    if (previousWinner) {
      setExcludedKeys((current) => new Set([...Array.from(current), previousWinner.key]));
    }

    if (!availablePool.length) {
      setDrawState((current) => ({
        ...current,
        [definition.id]: { status: 'empty' }
      }));
      return;
    }

    clearTimers();
    setDrawState((current) => ({
      ...current,
      [definition.id]: { status: 'running' }
    }));

    timeoutRef.current = window.setTimeout(() => {
      clearTimers();
      const winner = availablePool[Math.floor(Math.random() * availablePool.length)];
      setDrawState((current) => ({
        ...current,
        [definition.id]: winner ? { status: 'complete', entrant: winner } : { status: 'empty' }
      }));
    }, 3000);
  }

  return (
    <div className="-mx-1 -my-2 flex min-h-[calc(100vh-8rem)] flex-col gap-[clamp(1rem,1.8vw,1.75rem)] overflow-hidden px-2 py-3 md:-mx-4 md:-my-4 md:min-h-[calc(100vh-9rem)] md:px-4 md:py-4">
      <div className="flex justify-center">
        <div className="inline-flex items-baseline gap-3 rounded-full bg-white px-[clamp(1.5rem,2vw,2.6rem)] py-[clamp(0.75rem,1vw,1.1rem)] text-[clamp(1.75rem,3.2vw,3.6rem)] font-bold text-zinc-900 shadow-[inset_0_0_0_1px_rgba(212,212,216,1)]">
          <span>Total responses</span>
          <strong className="text-[1.25em] leading-none text-accent">{pools.overall.length}</strong>
        </div>
      </div>

      {error ? (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-xl border border-zinc-200 bg-white px-3 py-2 text-center text-sm font-medium text-zinc-600">
          Loading responses...
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-[clamp(1rem,1.5vw,1.6rem)] sm:grid-cols-2 lg:grid-cols-6 lg:grid-rows-2">
        {DRAW_DEFINITIONS.map((definition, index) => (
          <div
            key={definition.id}
            className={classNames(
              'min-h-0 lg:col-span-2',
              index === 3 ? 'lg:col-start-2' : '',
              index === 4 ? 'lg:col-start-4' : ''
            )}
          >
            <PrizeBox
              definition={definition}
              state={drawState[definition.id]}
              disabled={loading || drawInProgress}
              onDraw={() => runDraw(definition)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
